
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  UserRound,
  Stethoscope,
  ClipboardCheck,
  ShoppingCart,
  CheckCircle2,
} from "lucide-react";

import { usePrescriptionCart } from "../../context/pharmacy/PrescriptionCartContext";
import "./InsurancePrescriptionForm.css";

const insuranceOptions = [
  {
    value: " تامین اجتماعی",
    label: "بیمه تامین اجتماعی",
    description: "سازمان تامین اجتماعی",
  },
  {
    value: "سلامت",
    label: "بیمه سلامت / خدمات درمانی",
    description: "سازمان بیمه سلامت ایران",
  },
];

export default function InsurancePrescriptionForm() {
  const navigate = useNavigate();
  const { addItem } = usePrescriptionCart();

  const [insuranceType, setInsuranceType] = useState("");
  const [nationalCode, setNationalCode] = useState("");
  const [doctorName, setDoctorName] = useState("");
  const [trackingCode, setTrackingCode] = useState("");
  const [error, setError] = useState("");

  const handleAdd = (event) => {
    event.preventDefault();

    if (!insuranceType || !nationalCode.trim() ||
        !doctorName.trim() || !trackingCode.trim()) {
      setError("لطفاً تمام فیلدها را تکمیل کنید.");
      return;
    }

    if (!/^\d{10}$/.test(nationalCode.trim())) {
      setError("کد ملی باید شامل ۱۰ رقم باشد.");
      return;
    }

    addItem({
      type: "insurancePrescription",
      insuranceType: insuranceType.trim(),
      nationalCode: nationalCode.trim(),
      doctorName: doctorName.trim(),
      trackingCode: trackingCode.trim(),
    });

    navigate("/dashboard/pharmacy/prescription-cart");
  };

  return (
    <section className="insurance-prescription" dir="rtl">
      <header className="insurance-form-header">
        <div className="insurance-header-icon">
          <ShieldCheck size={28} />
        </div>

        <div>
          <h2>نسخه الکترونیک بیمه</h2>
          <p>
            نوع بیمه را انتخاب کرده و اطلاعات نسخه را وارد کنید.
          </p>
        </div>
      </header>

      <form onSubmit={handleAdd} noValidate>
        <fieldset className="insurance-options-fieldset">
          <legend>
            نوع بیمه <span className="insurance-required">*</span>
          </legend>

          <div className="insurance-options">
            {insuranceOptions.map((option) => {
              const selected = insuranceType === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  className={`insurance-option ${
                    selected ? "selected" : ""
                  }`}
                  onClick={() => {
                    setInsuranceType(option.value);
                    setError("");
                  }}
                  aria-pressed={selected}
                >
                  <span className="insurance-option-check">
                    {selected && <CheckCircle2 size={20} />}
                  </span>

                  <span className="insurance-option-icon">
                    <ShieldCheck size={25} />
                  </span>

                  <span className="insurance-option-content">
                    <strong>{option.label}</strong>
                    <small>{option.description}</small>
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="insurance-fields-heading">
          <h3>اطلاعات نسخه</h3>
          <span>فیلدهای ستاره‌دار الزامی هستند.</span>
        </div>

        <div className="insurance-fields-grid">
          <div className="insurance-field">
            <label htmlFor="insurance-national-code">
              <UserRound size={17} />
              کد ملی <span className="insurance-required">*</span>
            </label>

            <input
              id="insurance-national-code"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              dir="ltr"
              maxLength={10}
              placeholder="کد ملی ۱۰ رقمی"
              value={nationalCode}
              onChange={(e) => {
                setNationalCode(e.target.value.replace(/\D/g, "").slice(0, 10));
                setError("");
              }}
            />
          </div>

          <div className="insurance-field">
            <label htmlFor="insurance-doctor-name">
              <Stethoscope size={17} />
              نام پزشک <span className="insurance-required">*</span>
            </label>

            <input
              id="insurance-doctor-name"
              type="text"
              autoComplete="off"
              placeholder="نام و نام خانوادگی پزشک"
              value={doctorName}
              onChange={(e) => {
                setDoctorName(e.target.value);
                setError("");
              }}
            />
          </div>

          <div className="insurance-field insurance-field-full">
            <label htmlFor="insurance-tracking-code">
              <ClipboardCheck size={17} />
              کد رهگیری نسخه <span className="insurance-required">*</span>
            </label>

            <input
              id="insurance-tracking-code"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              dir="ltr"
              placeholder="کد رهگیری نسخه الکترونیک"
              value={trackingCode}
              onChange={(e) => {
                setTrackingCode(e.target.value);
                setError("");
              }}
            />

            <small className="insurance-field-hint">
              کد رهگیری درج‌شده روی نسخه الکترونیک را وارد کنید.
            </small>
          </div>
        </div>

        {error && (
          <p className="insurance-error" role="alert">
            {error}
          </p>
        )}

        <div className="insurance-form-actions">
          <button
            type="submit"
            className="insurance-add-button"
          >
            <ShoppingCart size={20} />
            افزودن نسخه به سبد خرید
          </button>
        </div>
      </form>

      <aside className="insurance-notice">
        <div className="insurance-notice-title">
          <ShieldCheck size={19} />
          راهنمای ثبت نسخه
        </div>

        <ul>
          <li>نوع بیمه را مطابق بیمه ثبت‌شده بیمار انتخاب کنید.</li>
          <li>کد ملی و کد رهگیری را با دقت وارد کنید.</li>
          <li>اطلاعات واردشده را پیش از افزودن به سبد خرید بررسی کنید.</li>
        </ul>
      </aside>
    </section>
  );
}
